import type { BessConfig, TelemetryKey } from "./types";

/**
 * TelemetryKey plus the short human name used as primary text in the
 * telemetry browser, chart series names and card titles (BESS-10 /
 * CLAR-14). The raw `key` stays the wire/topic identifier and is shown
 * as mono secondary text for operators who need it.
 */
export interface LabeledTelemetryKey extends TelemetryKey {
  label: string;
}

/**
 * Build the catalog of historical telemetry keys for a single BESS unit.
 * Used by the BESS-detail telemetry browser. Keys are sourced from
 * battery-communication-service publishes plus controller setpoint topics.
 */
export function bessTelemetryCatalog(b: BessConfig): LabeledTelemetryKey[] {
  const T = b.topics;
  const list: LabeledTelemetryKey[] = [
    {
      key: "ac_active_power",
      label: "Active power",
      topic: T.ac_active_power,
      group: "power",
      unit: "W",
      chart: "line",
      description: "AC active power. Positive = charging, negative = discharging.",
      signed: true,
    },
    {
      key: "ac_reactive_power",
      label: "Reactive power",
      topic: T.ac_reactive_power,
      group: "power",
      unit: "VAr",
      chart: "line",
      description: "AC reactive power.",
      signed: true,
    },
    {
      key: "soc",
      label: "State of charge",
      topic: T.soc,
      group: "soc",
      unit: "%",
      chart: "line",
      description: "State of charge.",
      signed: false,
    },
    {
      key: "total_voltage",
      label: "DC bus voltage",
      topic: T.total_voltage,
      group: "voltage",
      unit: "V",
      chart: "line",
      description: "DC bus total voltage.",
      signed: false,
    },
    {
      key: "ambient_temperature",
      label: "Ambient temperature",
      topic: T.ambient_temperature,
      group: "temperature",
      unit: "°C",
      chart: "line",
      description: "Ambient enclosure temperature.",
      signed: false,
    },
    {
      key: "accumulated_charge",
      label: "Lifetime charge energy",
      topic: T.accumulated_charge,
      group: "energy",
      unit: "kWh",
      chart: "line",
      description: "Lifetime accumulated charge energy.",
      signed: false,
    },
    {
      key: "accumulated_discharge",
      label: "Lifetime discharge energy",
      topic: T.accumulated_discharge,
      group: "energy",
      unit: "kWh",
      chart: "line",
      description: "Lifetime accumulated discharge energy.",
      signed: false,
    },
    {
      key: "setpoint_active",
      label: "Commanded power",
      topic: T.setpoint_active,
      group: "command",
      unit: "W",
      chart: "step",
      description: "Active power setpoint commanded by controller.",
      signed: true,
    },
    {
      key: "setpoint_reactive",
      label: "Commanded reactive power",
      topic: T.setpoint_reactive,
      group: "command",
      unit: "VAr",
      chart: "step",
      description: "Reactive power setpoint commanded by controller.",
      signed: true,
    },
    {
      key: "bess_onoff",
      label: "On/off state",
      topic: T.bess_onoff,
      group: "command",
      unit: "bool",
      chart: "step",
      description: "BESS ON/OFF command.",
      signed: false,
    },
    {
      key: "dc_relays",
      label: "DC relays",
      topic: T.dc_relays,
      group: "command",
      unit: "bool",
      chart: "step",
      description: "DC relay command.",
      signed: false,
    },
    {
      key: "fault_count",
      label: "Fault flags",
      topic: `${T.fault_flags_prefix}*`,
      group: "fault",
      unit: "count",
      chart: "step",
      description: "Active fault flag count (rolled up).",
      signed: false,
    },
    {
      key: "heartbeat_age",
      label: "Heartbeat age",
      topic: `services/battery-comm-service/${b.id}/heartbeat`,
      group: "diagnostic",
      unit: "s",
      chart: "line",
      description: "Service heartbeat age. Spikes indicate broker or service stalls.",
      signed: false,
    },
  ];
  return list;
}

export const TELEMETRY_GROUPS = [
  "power",
  "energy",
  "soc",
  "voltage",
  "current",
  "temperature",
  "fault",
  "command",
  "diagnostic",
] as const;
